import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  PolygonSymbolizer,
  type LabelRule,
  type PaintRule,
} from "protomaps-leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import {
  PointCircleSymbolizer,
  PointLabelSymbolizer,
} from "@/features/map/utils/pointSymbolizer";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

export enum SchoolType {
  ES,
  JHS,
}

interface Props {
  defaultVisible: boolean;
  visible: boolean; // 外部から制御される表示状態
  layerName: string; // レイヤー名(leaflet用)
  dataLayerName: string; // pmtilesに書き込まれているレイヤー名
  layerUrl: string; // URL
  schoolType: SchoolType; // 小学校か中学校か指定する
  polygonStrokeColor?: string; // ポリゴンの色を指定
  circleFillColor?: string; // 丸点の色を指定
}

// UIに表示するレイヤー名
export const ELEMENTARY_SCHOOL_AREA_LAYER_NAME = "小学校区";
export const JUNIOR_HIGH_SCHOOL_AREA_LAYER_NAME = "中学校区";

// pmtilesが内部で保持しているレイヤー名
export const ELEMENTARY_SCHOOL_AREA_DATA_LAYER_NAME = "es_school_area_tile";
export const JUNIOR_HIGH_SCHOOL_AREA_DATA_LAYER_NAME = "jh_school_area_tile";

// 学校区タイルの配信ディレクトリ
const SCHOOL_AREA_TILE_DIR = "school-area-tiles";

// 年度別配信より前に作成した 2023 年のタイルは、ファイル名・内部レイヤー名の
// 命名規則が他年度（`{year}_xx_school_area_tile`）と異なるため個別に扱う
const LEGACY_TILE_YEAR = 2023;

/**
 * 小学校区タイルの年度別URLを取得する
 */
export const getEsSchoolAreaTileUrl = (year: number, host: string): string => {
  if (year === LEGACY_TILE_YEAR) {
    return `${host}/${SCHOOL_AREA_TILE_DIR}/es_school_area_tile_z17_injected.pmtiles`;
  }
  return `${host}/${SCHOOL_AREA_TILE_DIR}/${year}_es_school_area_tile.pmtiles`;
};

/**
 * 中学校区タイルの年度別URLを取得する
 */
export const getJhSchoolAreaTileUrl = (year: number, host: string): string => {
  if (year === LEGACY_TILE_YEAR) {
    return `${host}/${SCHOOL_AREA_TILE_DIR}/jh_school_area_tile_z17_injected.pmtiles`;
  }
  return `${host}/${SCHOOL_AREA_TILE_DIR}/${year}_jhs_school_area_tile.pmtiles`;
};

/**
 * 小学校区タイルの年度別データレイヤー名（内部レイヤー名）を取得する
 */
export const getEsSchoolAreaDataLayerName = (year: number): string => {
  if (year === LEGACY_TILE_YEAR) {
    return ELEMENTARY_SCHOOL_AREA_DATA_LAYER_NAME;
  }
  return `${year}_${ELEMENTARY_SCHOOL_AREA_DATA_LAYER_NAME}`;
};

/**
 * 中学校区タイルの年度別データレイヤー名（内部レイヤー名）を取得する
 */
export const getJhSchoolAreaDataLayerName = (year: number): string => {
  if (year === LEGACY_TILE_YEAR) {
    return JUNIOR_HIGH_SCHOOL_AREA_DATA_LAYER_NAME;
  }
  return `${year}_${"jhs_school_area_tile"}`;
};

// ズームレベル
// pmtiles生成時に使ったズームレベルの値と同じでなくても問題ない
// ポリゴンの表示ズームレベルの設定、ポリゴンはハザードレイヤーと同一ズームレベルで表示する
export const SCHOOL_AREA_MIN_ZOOM = 14;
export const SCHOOL_AREA_MAX_ZOOM = 22;

const POLYGON_MIN_ZOOM = SCHOOL_AREA_MIN_ZOOM;
const POLYGON_MAX_ZOOM = SCHOOL_AREA_MAX_ZOOM;

const LABEL_MIN_ZOOM = SCHOOL_AREA_MIN_ZOOM;
const LABEL_MAX_ZOOM = SCHOOL_AREA_MAX_ZOOM;

const CIRCLE_MIN_ZOOM = SCHOOL_AREA_MIN_ZOOM;
const CIRCLE_MAX_ZOOM = SCHOOL_AREA_MAX_ZOOM;

// ポリゴンの線のスタイルを設定
// 色を透過にすると重なった部分で見かけの色が変わってしまい見づらいので不透明としている
const DEFAULT_POLYGON_OPACITY = 1;
const DEFAULT_POLYGON_STOROKE_COLOR = "blue";
const DEFAULT_POLYGON_WIDTH = 4;

// 丸い点のスタイルを設定
const DEFAULT_CIRCLE_RADIUS = 6;
const DEFAULT_CIRCLE_WIDTH = 1;
const DEFAULT_CIRCLE_FILL_COLOR = "red";
const DEFAULT_CIRCLE_STOROKE_COLOR = "black";
const DEFAULT_CIRCLE_OPACITY = 0.75;

// 学校レイヤーの重なり順を設定
const SCHOOL_LAYER_Z_INDEX = LAYER_Z_INDEX.SCHOOL_AREA;

// 右下に表示する項目
const attribution =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

interface SchoolAreaRuleOptions {
  dataLayerName: string;
  schoolType: SchoolType;
  polygonStrokeColor: string;
  circleFillColor: string;
}

export const buildSchoolAreaRules = (
  {
    dataLayerName,
    schoolType,
    polygonStrokeColor,
    circleFillColor,
  }: SchoolAreaRuleOptions,
  renderScale: number,
  zoomOffset: number
): { paintRules: PaintRule[]; labelRules: LabelRule[] } => ({
  labelRules: [
    {
      dataLayer: dataLayerName,
      symbolizer: new PointLabelSymbolizer({
        textGetter: (properties) => {
          if (schoolType === SchoolType.ES) {
            const val = properties.A27_004;
            return val != null ? String(val) : "";
          }
          if (schoolType === SchoolType.JHS) {
            // 2016年はA32_003に学校名が入っている
            const val = dataLayerName.includes("2016")
              ? properties.A32_003
              : properties.A32_004;
            return val != null ? String(val) : "";
          }
          return "";
        },
        drawRect: false,
        fillRect: true,
        color: "white",
        renderScale,
      }),
      minzoom: LABEL_MIN_ZOOM + zoomOffset,
      maxzoom: LABEL_MAX_ZOOM + zoomOffset,
    },
  ],
  paintRules: [
    {
      dataLayer: dataLayerName,
      symbolizer: new PolygonSymbolizer({
        fill: "transparent",
        opacity: DEFAULT_POLYGON_OPACITY,
        stroke: polygonStrokeColor,
        width: DEFAULT_POLYGON_WIDTH * renderScale,
      }),
      minzoom: POLYGON_MIN_ZOOM + zoomOffset,
      maxzoom: POLYGON_MAX_ZOOM + zoomOffset,
    },
    {
      dataLayer: dataLayerName,
      symbolizer: new PointCircleSymbolizer({
        radius: DEFAULT_CIRCLE_RADIUS * renderScale,
        width: DEFAULT_CIRCLE_WIDTH * renderScale,
        fill: circleFillColor,
        stroke: DEFAULT_CIRCLE_STOROKE_COLOR,
        opacity: DEFAULT_CIRCLE_OPACITY,
      }),
      minzoom: CIRCLE_MIN_ZOOM + zoomOffset,
      maxzoom: CIRCLE_MAX_ZOOM + zoomOffset,
    },
  ],
});

// 学校区レイヤー
export const SchoolAreaLayer: React.FC<Props> = (props) => {
  const {
    defaultVisible,
    visible,
    layerName,
    dataLayerName,
    layerUrl,
    schoolType,
    polygonStrokeColor = DEFAULT_POLYGON_STOROKE_COLOR,
    circleFillColor = DEFAULT_CIRCLE_FILL_COLOR,
  } = props;

  const map = useMap();
  const [internalVisible, setInternalVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer => {
      const rules = buildSchoolAreaRules(
        {
          dataLayerName,
          schoolType,
          polygonStrokeColor,
          circleFillColor,
        },
        context.printScale,
        context.zoomOffset
      );
      return leafletLayer({
        url: layerUrl,
        ...rules,
        attribution,
      }) as GridLayer;
    },
    [layerUrl, dataLayerName, schoolType, polygonStrokeColor, circleFillColor]
  );

  useProtomapsPrintAdapter({
    map,
    enabled: internalVisible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  // 外部から渡されるvisibleプロパティの変更を監視して内部状態を同期
  useEffect(() => {
    setInternalVisible(visible);
  }, [visible]);

  // sentryレビュー対応
  // map.off()実行時に削除対象の関数を渡しておく必要がある。
  // そうしないとmap.off()で対象のハンドラに登録された全ての関数(こことは関係ない別の関数も)が削除されてしまう。
  // そのため、map.on(), map.off()で匿名関数を渡すのをやめて、下記useCallbackで定義した関数を明示的に渡すようにする。
  const overlayAddHandler = useCallback(
    (e: LayersControlEvent) => {
      if (e.name === layerName) {
        setInternalVisible(true);
      }
    },
    [layerName]
  );

  const overlayRemoveHandler = useCallback(
    (e: LayersControlEvent) => {
      if (e.name === layerName) {
        setInternalVisible(false);
      }
    },
    [layerName]
  );

  // レイヤーコントロールのイベントで表示・非表示を切り替える
  useEffect(() => {
    if (!map) return;
    map.on("overlayadd", overlayAddHandler);
    map.on("overlayremove", overlayRemoveHandler);

    return () => {
      if (!map) return;
      map.off("overlayadd", overlayAddHandler);
      map.off("overlayremove", overlayRemoveHandler);
    };
  }, [map, overlayAddHandler, overlayRemoveHandler]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    if (internalVisible) {
      if (!map.hasLayer(layer)) {
        layer.addTo(map);
      }
    } else {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    }
  }, [internalVisible, map]);

  useEffect(() => {
    if (!map) return;

    const { paintRules, labelRules } = buildSchoolAreaRules(
      {
        dataLayerName,
        schoolType,
        polygonStrokeColor,
        circleFillColor,
      },
      1,
      0
    );

    const layer = leafletLayer({
      url: layerUrl,
      paintRules,
      labelRules,
      attribution,
    }) as GridLayer;

    layer.setZIndex(SCHOOL_LAYER_Z_INDEX);

    layerRef.current = layer;

    // 初期表示状態を適用
    if (internalVisible) {
      layer.addTo(map);
    }

    return () => {
      if (map && layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
    // internalVisibleを依存配列に含めないことで、表示/非表示の切り替え時にレイヤーが再作成されないようにする
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    layerUrl,
    dataLayerName,
    schoolType,
    polygonStrokeColor,
    circleFillColor,
  ]);

  return <FeatureGroup />;
};
